import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckClimaEl } from '~/pages/Farmer/Co2Climate/style'
import { Accordion, Button, Icon, Transition } from 'semantic-ui-react'
import { useFormContext, useWatch } from 'react-hook-form'
import AssignFeedModal from './AssignFeedModal'
import { FeedWrapper, StyledDivider, StyledFeed, FeedGroupsWrapper, StyledAccordion } from '../style'
import InfoPopup from '~/components/InfoPopup'
import cloneDeep from 'lodash.clonedeep'
import Dialog from '~/components/Dialog'
import { getNumberOfFeeds, getTransformFeedGroups } from '../../../utils'
import RearingPhaseAssign from './RearingPhaseAssign'
import { CO2OptionsType } from '~/api/services/co2'

interface Props {
  milkOptions: CO2OptionsType
}

const AssignFeedPage: React.FC<Props> = ({ milkOptions }) => {
  const { control, setValue } = useFormContext()
  const { t } = useTranslation()

  const [activeAccordionIndex, setActiveAccordionIndex] = useState(0)
  const [isOpenFeedModal, setIsOpenFeedModal] = useState(false)
  const [feedsTransformed, setFeedTransformed] = useState([])
  const [deleteFeedIndex, setDeleteFeedIndex] = useState(-1)
  const [isOpenPhaseAssign, setIsOpenPhaseAssign] = useState(false)

  const watchPhaseEnabled = useWatch({ control, name: 'phaseFeeding' })
  const watchFeedGroups = useWatch({ control, name: 'feedGroups' })
  const watchRearingPhases = useWatch({ control, name: 'rearingPhases' })
  const watchRearingPhasesEnabled = useWatch({ control, name: 'raiseHeifersOnFarm' })
  const watchRearingPhasesType = useWatch({ control, name: 'rearingPhasesType' })

  const feedOptionsCows = [
    {
      title: t('Coarse Feeds'),
      key: 'cowsCoarseFeeds',
      feedKey: 'coarseFeeds'
    },
    {
      title: t('Power and mineral feed'),
      key: 'cowsConcentratedFeeds',
      feedKey: 'coarseFeeds'
    },
    {
      title: t('Juicy Feeds (Only one feed can be selected)'),
      key: 'juicyFeeds',
      feedKey: 'juicyFeeds',
      info: t('Aufgrund der komplexen Rechenbasis lässt sich nur ein Saftfuttermittel in die Kalkulation eintragen. Bitte wählen Sie daher das vorrangig genutzte Futtermittel aus. ')
    }
  ]

  const feedOptionsCalves = [
    {
      title: t('Coarse Feeds'),
      key: 'youngCattleCoarseFeeds',
      feedKey: 'coarseFeeds'
    },
    {
      title: t('Power and mineral feed'),
      key: 'youngCattleConcentratedFeeds',
      feedKey: 'concentratedFeeds'
    }
  ]

  useEffect(() => {
    handleCheckFeedErrors()
    if (watchPhaseEnabled) {
      const feedsTransformed = getTransformFeedGroups(watchFeedGroups)
      setFeedTransformed(feedsTransformed)
    } else {
      setFeedTransformed(watchFeedGroups)
    }
  }, [watchPhaseEnabled, watchFeedGroups])

  function handleCheckFeedErrors() {
    feedsTransformed.forEach(feedGroup => {
      const numberOfFeeds = getNumberOfFeeds(feedGroup)
      const element = document.getElementById(feedGroup['name'])
      if (element && element?.classList?.contains('error-feed-group') && numberOfFeeds > 0) {
        element.classList.remove('error-feed-group')
      }
    })
  }

  function handleAccordionClick(e, titleProps) {
    const { index } = titleProps
    setActiveAccordionIndex(activeAccordionIndex === index ? -1 : index);
  }

  function renderFeeds(feeds, title) {
    return <FeedWrapper style={{ marginBottom: 10 }}>
      <div className='title'>{title}</div>
      <StyledDivider />
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {feeds.map(feedOption => {
          return <StyledFeed
            key={feedOption.label}
            $smallFont
            basic>{feedOption.label}</StyledFeed>
        })}
      </div>
    </FeedWrapper>
  }

  function renderConcatratedFeeds(feeds, headerTitle) {
    return <FeedWrapper style={{ marginLeft: 20 }}>
      <div style={{ width: 'fit-content' }}>
        <div className='feed-title'>{headerTitle}</div>
        <StyledDivider />
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {feeds.map(feedOption => {
          return <StyledFeed
            key={feedOption.label}
            $smallFont
            basic>{feedOption.label}</StyledFeed>
        })}
      </div>
    </FeedWrapper>
  }

  function parseConcentratedFeeds(concentratedFeeds) {
    return concentratedFeeds.reduce((acc, item) => {
      const { group } = item;
      if (!acc[group]) {
        acc[group] = [];
      }
      acc[group].push(item);
      return acc;
    }, {});
  }

  function renderFeedGroupItems(feedGroup, feedGroupIndex) {
    return <div>
      {feedOptionsCows.map(feedOption => {
        if (!feedOption.key.includes('oncentratedFeeds') && feedGroup[feedOption.feedKey]?.length > 0) {
          return renderFeeds(feedGroup[feedOption.feedKey], feedOption.title)
        }
        if (feedOption.key.includes('oncentratedFeeds') && feedGroup?.concentratedFeeds?.length > 0) {
          const groupConcentratedFeeds = parseConcentratedFeeds(feedGroup?.concentratedFeeds)
          return <FeedWrapper style={{ marginBottom: 10 }}>
            <div className='title'>{feedOption.title}</div>
            <StyledDivider />
            {Object.keys(groupConcentratedFeeds).map((keyIndex) => {
              const feedHasItems = groupConcentratedFeeds[keyIndex]?.length > 0
              const headerTitle = Object.keys(milkOptions.cowsConcentratedFeeds)[parseInt(keyIndex) - 1]
              return feedHasItems && renderConcatratedFeeds(groupConcentratedFeeds[keyIndex], headerTitle)
            })}
          </FeedWrapper>
        }
      })}
      <div style={{ display: 'flex', justifyContent: 'end' }}>
        <Button
          onClick={() => setIsOpenFeedModal(true)}
          positive
          style={{ color: 'white', float: 'rigth', marginTop: 10 }}
          type='button'>{t('Select feeds')}</Button>
        <Button
          negative
          disabled={getNumberOfFeeds(feedsTransformed[feedGroupIndex]) === 0}
          onClick={() => {
            const index = feedGroup?.feedGroupIndex !== undefined ? feedGroup.feedGroupIndex : feedGroupIndex
            setDeleteFeedIndex(index)
          }}
          style={{ color: 'white', float: 'rigth', marginTop: 10 }}
          type='button'>{t('Remove feed')}</Button>
      </div>
    </div>
  }

  function renderFeedAssignModal() {
    return <AssignFeedModal
      isOpenFeedModal={isOpenFeedModal}
      handleClose={(submited) => {
        setIsOpenFeedModal(false)
        if (submited) {
          if (activeAccordionIndex + 1 < (feedsTransformed.length + watchRearingPhases.length)) {
            setActiveAccordionIndex(activeAccordionIndex + 1)
          } else {
            setActiveAccordionIndex(-1)
          }
        }
      }}
      milkOptions={milkOptions}
      feed={feedsTransformed[activeAccordionIndex]}
      index={activeAccordionIndex}
    />
  }

  function handleDeleteFeeds() {
    const feedGroup = cloneDeep(feedsTransformed[activeAccordionIndex])
    if (deleteFeedIndex >= 0 && watchFeedGroups[deleteFeedIndex]?.feedingPhases?.length > 0) {
      const tempArray = cloneDeep(watchFeedGroups)
      const phaseIndex = feedGroup.order - 1
      const feedPhaseIndex = feedGroup?.feedGroupIndex
      if (phaseIndex >= 0 && feedPhaseIndex >= 0) {
        tempArray[feedPhaseIndex].feedingPhases[phaseIndex] = {
          ...tempArray[feedPhaseIndex].feedingPhases[phaseIndex],
          coarseFeeds: [],
          concentratedFeeds: [],
          juicyFeeds: []
        }
      }
      setValue('feedGroups', tempArray)
    } else {
      const tempArray = cloneDeep(watchFeedGroups)
      tempArray[deleteFeedIndex] = {
        ...tempArray[deleteFeedIndex],
        coarseFeeds: [],
        concentratedFeeds: [],
        juicyFeeds: [],
        feedingPhases: []
      }
      setValue('feedGroups', tempArray)
    }
    setDeleteFeedIndex(-1)
  }

  function handleDeleteRearingPhases() {
    const rearingPhases = cloneDeep(watchRearingPhases)
    const phaseIndex = deleteFeedIndex - feedsTransformed.length
    rearingPhases[phaseIndex] = {
      ...rearingPhases[phaseIndex],
      coarseFeeds: [],
      concentratedFeeds: []
    }
    setValue('rearingPhases', rearingPhases)
    setDeleteFeedIndex(-1)
  }

  function renderDeleteFeedsModal() {
    return <Dialog
      isDialogOpen={deleteFeedIndex >= 0}
      handleClose={() => setDeleteFeedIndex(-1)}
      textHeader={t('Remove feed')}
      textContent={<div dangerouslySetInnerHTML={{ __html: t('Are you sure you want to remove all feed from this performance group {{name}}?', { name: feedsTransformed[activeAccordionIndex]?.name }) }} />}
      textSubmit={t('Remove feed')}
      textClose={t('Cancel')}
      handleSubmit={() => {
        if (deleteFeedIndex < feedsTransformed.length) {
          handleDeleteFeeds()
          return
        }
        handleDeleteRearingPhases()
      }}
      type='abort'
    />
  }

  function renderCowFeeds() {
    return feedsTransformed.map((feedGroup, index) => {
      const active = activeAccordionIndex === index
      const numberOfFeeds = getNumberOfFeeds(feedGroup)
      return < StyledAccordion
        styled
        style={{ width: 'calc(100% - 40px)', margin: 10 }}
        key={feedGroup?.key}
        id={feedGroup['name']}
      >
        <Accordion.Title
          index={index}
          active={active}
          onClick={handleAccordionClick}>
          <div className='accordion-title'>
            {`${feedGroup['name']}`}
            <div>({numberOfFeeds === 0 ? t('Please select feed rations') : t('Feed rations: {{numberOfFeeds}}', { numberOfFeeds })})</div>
          </div>
          <Icon name={active ? 'minus' : 'plus'} />
        </Accordion.Title>
        <Transition visible={active} animation='slide down' duration={200}>
          <Accordion.Content
            active={active}
            content={renderFeedGroupItems(feedGroup, index)}
          />
        </Transition>
      </StyledAccordion>
    })
  }

  function renderRearingFeedGroupItems(rearingPhase, phaseIndex) {
    return <div>
      {feedOptionsCalves.map(feedOption => {
        if (!feedOption.key.includes('oncentratedFeeds') && rearingPhase[feedOption.feedKey]?.length > 0) {
          return renderFeeds(rearingPhase[feedOption.feedKey], feedOption.title)
        }
        if (feedOption.key.includes('oncentratedFeeds') && rearingPhase?.concentratedFeeds?.length > 0) {
          const groupConcentratedFeeds = parseConcentratedFeeds(rearingPhase?.concentratedFeeds)
          return <FeedWrapper style={{ marginBottom: 10 }}>
            <div className='title'>{feedOption.title}</div>
            <StyledDivider />
            {Object.keys(groupConcentratedFeeds).map((keyIndex) => {
              const feedHasItems = groupConcentratedFeeds[keyIndex]?.length > 0
              const headerTitle = Object.keys(milkOptions.youngCattleConcentratedFeeds)[parseInt(keyIndex) - 1]
              return feedHasItems && renderConcatratedFeeds(groupConcentratedFeeds[keyIndex], headerTitle)
            })}
          </FeedWrapper>
        }
      })}
      <div style={{ display: 'flex', justifyContent: 'end' }}>
        <Button
          onClick={() => setIsOpenPhaseAssign(true)}
          positive
          style={{ color: 'white', float: 'rigth', marginTop: 10 }}
          type='button'>{t('Select feeds')}</Button>
        <Button
          negative
          disabled={getNumberOfFeeds(watchRearingPhases[phaseIndex]) === 0}
          onClick={() => {
            setDeleteFeedIndex(phaseIndex + feedsTransformed.length)
          }}
          style={{ color: 'white', float: 'rigth', marginTop: 10 }}
          type='button'>{t('Remove feed')}</Button>
      </div>
    </div>
  }

  function renderRearingPhases() {
    return watchRearingPhases.map((rearingPhase, index) => {
      const feedIndex = feedsTransformed.length + index
      const active = activeAccordionIndex === feedIndex
      const numberOfFeeds = getNumberOfFeeds(rearingPhase)
      return < StyledAccordion
        styled
        style={{ width: 'calc(100% - 40px)', margin: 10 }}
        key={rearingPhase?.key}
        id={rearingPhase['name']}
      >
        <Accordion.Title
          index={feedIndex}
          active={active}
          onClick={handleAccordionClick}>
          <div className='accordion-title'>
            {`${rearingPhase['name']} (${rearingPhase['period']} Monate)`}
            <div>({numberOfFeeds === 0 ? t('Please select feed rations') : t('Feed rations: {{numberOfFeeds}}', { numberOfFeeds })})</div>
          </div>
          <Icon name={active ? 'minus' : 'plus'} />
        </Accordion.Title>
        <Transition visible={active} animation='slide down' duration={200}>
          <Accordion.Content
            active={active}
            content={renderRearingFeedGroupItems(rearingPhase, index)}
          />
        </Transition>
      </StyledAccordion>
    })
  }

  function renderPhaseAssignModal() {
    const rearingPhaseIndex = activeAccordionIndex - feedsTransformed.length
    return <RearingPhaseAssign
      isOpenFeedModal={isOpenPhaseAssign}
      handleClose={(submited) => {
        setIsOpenPhaseAssign(false)
        if (submited) {
          if (activeAccordionIndex + 1 < (feedsTransformed.length + watchRearingPhases.length)) {
            setActiveAccordionIndex(activeAccordionIndex + 1)
          } else {
            setActiveAccordionIndex(-1)
          }
        }
      }}
      milkOptions={milkOptions}
      feed={watchRearingPhases[rearingPhaseIndex]}
      index={rearingPhaseIndex}
    />
  }

  const displayRearingPhases = Boolean(watchRearingPhasesEnabled) && watchRearingPhasesType === 'individual'

  return <FeedGroupsWrapper>
    {isOpenFeedModal && renderFeedAssignModal()}
    {isOpenPhaseAssign && renderPhaseAssignModal()}
    {renderDeleteFeedsModal()}
    <div className='subSection' style={{ width: 'fit-content' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 5 }}>
        <div>{t('Assign feed to performance groups')}</div>
        <InfoPopup
          content={`Wenn Sie im Betrachtungszeitraum weitere Futtermittel verfüttert haben, 
            welche hier nicht aufgelistet sind, wählen Sie bitte das Produkt, 
            welches dem Ihren am nächsten entspricht. Anschließend sollten Sie dieses dann auch in den Mengenangaben der Futterration mit berücksichtigen.`}
          styleIcon={{ fontSize: '1.2em' }}
          position={'top center'}
        />
      </div>
      <CheckClimaEl.StyledDivider />
    </div>
    {renderCowFeeds()}
    {displayRearingPhases && renderRearingPhases()}
  </FeedGroupsWrapper >
}
export default AssignFeedPage